import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Link from '../../Global/Link/Link';
import { ReactSVG } from 'react-svg';
import FormBlock from '../../Blocks/FormBlock/FormBlock';
import useRegionSelector from '../../../hooks/useRegionSelector';

import './index.scss';

function Footer() {
  const { region } = useRegionSelector();
  const [regionSelected, setRegionSelected] = useState(region);
  const { datoCmsConfiguration: footer } = useStaticQuery(graphql`
    query Footer {
      datoCmsConfiguration {
        headline: formHeadline
        form {
          ... on DatoCmsForm {
            sourceUrl
            cssLink
            containerElementId
          }
        }
        formEu {
          ... on DatoCmsForm {
            sourceUrl
            cssLink
            containerElementId
          }
        }
        formGlobal {
          ... on DatoCmsForm {
            sourceUrl
            cssLink
            containerElementId
          }
        }
        backgroundImage: formBackgroundImage {
          url
          alt
        }
        logoFooter {
          url
          alt
        }
        extraPages {
          ... on DatoCmsPage {
            id
            title
            slug
            model {
              apiKey
            }
          }
        }
        ukInformation
        usInformation
        euInformation
        facebookUrl
        twitterUrl
        youtubeUrl
        linkedinUrl
        tiktokUrl
        facebookUrlEu
        twitterUrlEu
        youtubeUrlEu
        linkedinUrlEu
        tiktokUrlEu
        facebookUrlUs
        twitterUrlUs
        tiktokUrlUs
        instagramUrlUs
      }
    }
  `);

  const {
    headline,
    form,
    formEu,
    formGlobal,
    backgroundImage,
    logoFooter,
    ukInformation,
    usInformation,
    euInformation,
    facebookUrl,
    twitterUrl,
    youtubeUrl,
    linkedinUrl,
    tiktokUrl,
    facebookUrlEu,
    twitterUrlEu,
    youtubeUrlEu,
    linkedinUrlEu,
    tiktokUrlEu,
    facebookUrlUs,
    twitterUrlUs,
    tiktokUrlUs,
    instagramUrlUs,
    extraPages,
  } = footer;

  useEffect(() => {
    setRegionSelected(region);
  }, [region]);

  const regionTexts = { UK: ukInformation, GLOBAL: usInformation, EU: euInformation };
  const facebookUrlRegion = { UK: facebookUrl, GLOBAL: facebookUrlUs, EU: facebookUrlEu };
  const twitterUrlRegion = { UK: twitterUrl, GLOBAL: twitterUrlUs, EU: twitterUrlEu };
  const youtubeUrlRegion = { UK: youtubeUrl, GLOBAL: youtubeUrl, EU: youtubeUrlEu };
  const linkedinUrlRegion = { UK: linkedinUrl, GLOBAL: null, EU: linkedinUrlEu };
  const tiktokUrlRegion = { UK: tiktokUrl, GLOBAL: tiktokUrlUs, EU: tiktokUrlEu };
  const instagramUrlRegion = { UK: null, GLOBAL: instagramUrlUs, EU: null };

  const formByRegion = { UK: form, GLOBAL: formGlobal, EU: formEu };

  return (
    <>
      <FormBlock block={{ headline, backgroundImage, form: formByRegion[region] }} />

      <div className={`footer-container`} data-datocms-noindex>
        <div className="container">
          <div className="logo">
            <ReactSVG src={logoFooter.url} />
          </div>

          <div className="row-information">
            <div className="brief">
              <div className="regions">
                <span
                  className={`${regionSelected === 'UK' ? 'selected' : ''}`}
                  onClick={() => setRegionSelected('UK')}
                >
                  UK
                </span>
                <span
                  className={`${regionSelected === 'EU' ? 'selected' : ''}`}
                  onClick={() => setRegionSelected('EU')}
                >
                  EU
                </span>
                <span
                  className={`${regionSelected === 'GLOBAL' ? 'selected' : ''}`}
                  onClick={() => setRegionSelected('GLOBAL')}
                >
                  US
                </span>
              </div>

              <div className="text" dangerouslySetInnerHTML={{ __html: regionTexts[regionSelected] }} />
            </div>

            <div className="social">
              <div className="networks">
                {facebookUrlRegion[regionSelected] && (
                  <a href={facebookUrlRegion[regionSelected]} target="_blank">
                    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H42V38C42 40.2091 40.2091 42 38 42H0V10Z" fill="#FAF9F6" />
                      <path
                        d="M23.6667 23.2381H26.7619L28 18.2857H23.6667V15.8095C23.6667 14.5343 23.6667 13.3333 26.1429 13.3333H28V9.17333C27.5964 9.1201 26.0723 9 24.4628 9C21.1013 9 18.7143 11.0515 18.7143 14.819V18.2857H15V23.2381H18.7143V33.7619H23.6667V23.2381Z"
                        fill="#1EB48F"
                      />
                    </svg>
                  </a>
                )}

                {tiktokUrlRegion[regionSelected] && (
                  <a href={twitterUrlRegion[regionSelected]} target="_blank">
                    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H42V38C42 40.2091 40.2091 42 38 42H0V10Z" fill="#FAF9F6" />
                      <path
                        d="M23.9047 20.4696L31.3513 12H29.5873L23.1187 19.3525L17.956 12H12L19.8087 23.1194L12 32H13.764L20.5907 24.2338L26.044 32H32M14.4007 13.3016H17.1107L29.586 30.7624H26.8753"
                        fill="#1EB48F"
                      />
                    </svg>
                  </a>
                )}

                {youtubeUrlRegion[regionSelected] && (
                  <a href={youtubeUrlRegion[regionSelected]} target="_blank">
                    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H42V38C42 40.2091 40.2091 42 38 42H0V10Z" fill="#FAF9F6" />
                      <path
                        d="M31.5403 15.6599C31.2873 14.6129 30.5418 13.7883 29.5953 13.5085C27.8796 13 21 13 21 13C21 13 14.1204 13 12.4047 13.5085C11.4582 13.7884 10.7127 14.6129 10.4597 15.6599C10 17.5577 10 21.5172 10 21.5172C10 21.5172 10 25.4767 10.4597 27.3744C10.7127 28.4214 11.4582 29.2117 12.4047 29.4915C14.1204 30 21 30 21 30C21 30 27.8796 30 29.5953 29.4915C30.5418 29.2117 31.2873 28.4214 31.5403 27.3744C32 25.4767 32 21.5172 32 21.5172C32 21.5172 32 17.5577 31.5403 15.6599ZM18.75 25.1121V17.9223L24.5 21.5173L18.75 25.1121Z"
                        fill="#1EB48F"
                      />
                    </svg>
                  </a>
                )}

                {linkedinUrlRegion[regionSelected] && (
                  <a href={linkedinUrlRegion[regionSelected]} target="_blank">
                    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H42V38C42 40.2091 40.2091 42 38 42H0V10Z" fill="#FAF9F6" />
                      <path
                        d="M17.0291 29H13.2973V17.65H17.0291V29ZM15.1612 16.1017C13.9679 16.1017 13 15.1682 13 14.0412C13 13.4998 13.2277 12.9806 13.633 12.5978C14.0383 12.2151 14.588 12 15.1612 12C15.7344 12 16.2841 12.2151 16.6894 12.5978C17.0947 12.9806 17.3224 13.4998 17.3224 14.0412C17.3224 15.1682 16.3541 16.1017 15.1612 16.1017ZM30.996 29H27.2722V23.4749C27.2722 22.1581 27.2441 20.4695 25.332 20.4695C23.3918 20.4695 23.0945 21.9001 23.0945 23.38V29H19.3667V17.65H22.9458V19.1982H22.998C23.4963 18.3065 24.7133 17.3654 26.5289 17.3654C30.3057 17.3654 31 19.7143 31 22.7653V29H30.996Z"
                        fill="#1EB48F"
                      />
                    </svg>
                  </a>
                )}

                {tiktokUrlRegion[regionSelected] && (
                  <a href={tiktokUrlRegion[regionSelected]} target="_blank">
                    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H42V38C42 40.2091 40.2091 42 38 42H0V10Z" fill="#FAF9F6" />
                      <path
                        d="M21.8426 10.0183C23.0201 10 24.1885 10.0092 25.3569 10C25.4288 11.4025 25.9232 12.8325 26.9298 13.8225C27.9365 14.8399 29.3566 15.3074 30.7407 15.4633V19.1574C29.4465 19.1116 28.1432 18.8366 26.9658 18.2682C26.4535 18.0299 25.9771 17.7274 25.5097 17.4157C25.5007 20.0924 25.5187 22.769 25.4918 25.4365C25.4199 26.7198 25.0064 27.9939 24.2784 29.0481C23.101 30.8081 21.0607 31.9539 18.9665 31.9906C17.6812 32.0639 16.3959 31.7064 15.2994 31.0464C13.4838 29.9556 12.2075 27.9573 12.0188 25.8123C12.0008 25.354 11.9918 24.8956 12.0098 24.4465C12.1716 22.7048 13.0165 21.0365 14.3287 19.8999C15.8207 18.5799 17.9059 17.9474 19.8563 18.3232C19.8743 19.6799 19.8203 21.0365 19.8203 22.3932C18.9305 22.0998 17.8879 22.1823 17.106 22.7323C16.5397 23.1082 16.1083 23.6857 15.8836 24.3365C15.6949 24.804 15.7488 25.3173 15.7578 25.8123C15.9735 27.3156 17.3936 28.5806 18.9036 28.4431C19.9102 28.4339 20.8719 27.8381 21.3932 26.9673C21.564 26.6648 21.7528 26.3531 21.7618 25.9956C21.8516 24.3548 21.8157 22.7232 21.8247 21.0824C21.8337 17.3882 21.8157 13.7033 21.8426 10.0183Z"
                        fill="#1EB48F"
                      />
                    </svg>
                  </a>
                )}

                {instagramUrlRegion[regionSelected] && (
                  <a href={instagramUrlRegion[regionSelected]} target="_blank">
                    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H42V38C42 40.2091 40.2091 42 38 42H0V10Z" fill="#FAF9F6" />
                      <path
                        d="M23.0286 13.0012C23.7583 12.9984 24.488 13.0058 25.2176 13.0232L25.4116 13.0302C25.6356 13.0382 25.8566 13.0482 26.1236 13.0602C27.1876 13.1102 27.9136 13.2782 28.5506 13.5252C29.2106 13.7792 29.7666 14.1232 30.3226 14.6792C30.831 15.1788 31.2244 15.7832 31.4756 16.4502C31.7226 17.0872 31.8906 17.8142 31.9406 18.8782C31.9526 19.1442 31.9626 19.3662 31.9706 19.5902L31.9766 19.7842C31.9943 20.5134 32.002 21.2428 31.9996 21.9722L32.0006 22.7182V24.0282C32.003 24.758 31.9954 25.4877 31.9776 26.2172L31.9716 26.4112C31.9636 26.6352 31.9536 26.8562 31.9416 27.1232C31.8916 28.1872 31.7216 28.9132 31.4756 29.5502C31.2252 30.218 30.8317 30.8228 30.3226 31.3222C29.8225 31.8306 29.2179 32.224 28.5506 32.4752C27.9136 32.7222 27.1876 32.8902 26.1236 32.9402C25.8566 32.9522 25.6356 32.9622 25.4116 32.9702L25.2176 32.9762C24.4881 32.994 23.7583 33.0017 23.0286 32.9992L22.2826 33.0002H20.9736C20.2438 33.0027 19.5141 32.995 18.7846 32.9772L18.5906 32.9712C18.3532 32.9626 18.1158 32.9526 17.8786 32.9412C16.8146 32.8912 16.0886 32.7212 15.4506 32.4752C14.7833 32.2246 14.1788 31.8311 13.6796 31.3222C13.1706 30.8225 12.7768 30.2178 12.5256 29.5502C12.2786 28.9132 12.1106 28.1872 12.0606 27.1232C12.0494 26.8859 12.0394 26.6486 12.0306 26.4112L12.0256 26.2172C12.0071 25.4877 11.9988 24.758 12.0006 24.0282V21.9722C11.9978 21.2428 12.0051 20.5134 12.0226 19.7842L12.0296 19.5902C12.0376 19.3662 12.0476 19.1442 12.0596 18.8782C12.1096 17.8132 12.2776 17.0882 12.5246 16.4502C12.7759 15.7829 13.1705 15.1787 13.6806 14.6802C14.1795 14.171 14.7835 13.7768 15.4506 13.5252C16.0886 13.2782 16.8136 13.1102 17.8786 13.0602L18.5906 13.0302L18.7846 13.0252C19.5138 13.0068 20.2432 12.9985 20.9726 13.0002L23.0286 13.0012ZM22.0006 18.0002C20.6745 18.0002 19.4027 18.527 18.465 19.4647C17.5274 20.4024 17.0006 21.6741 17.0006 23.0002C17.0006 24.3263 17.5274 25.5981 18.465 26.5358C19.4027 27.4734 20.6745 28.0002 22.0006 28.0002C23.3267 28.0002 24.5984 27.4734 25.5361 26.5358C26.4738 25.5981 27.0006 24.3263 27.0006 23.0002C27.0006 21.6741 26.4738 20.4024 25.5361 19.4647C24.5984 18.527 23.3267 18.0002 22.0006 18.0002ZM22.0006 20.0002C22.3945 20.0002 22.7847 20.0777 23.1487 20.2284C23.5127 20.3791 23.8434 20.6 24.122 20.8786C24.4007 21.1571 24.6217 21.4878 24.7725 21.8517C24.9233 22.2157 25.001 22.6058 25.0011 22.9997C25.0011 23.3937 24.9236 23.7838 24.7729 24.1478C24.6222 24.5118 24.4013 24.8426 24.1228 25.1212C23.8442 25.3998 23.5135 25.6209 23.1496 25.7717C22.7856 25.9225 22.3955 26.0002 22.0016 26.0002C21.2059 26.0002 20.4429 25.6842 19.8803 25.1216C19.3176 24.5589 19.0016 23.7959 19.0016 23.0002C19.0016 22.2046 19.3176 21.4415 19.8803 20.8789C20.4429 20.3163 21.2059 20.0002 22.0016 20.0002M27.2516 16.5002C26.929 16.5131 26.6239 16.6504 26.4002 16.8832C26.1765 17.116 26.0516 17.4264 26.0516 17.7492C26.0516 18.0721 26.1765 18.3824 26.4002 18.6153C26.6239 18.8481 26.929 18.9853 27.2516 18.9982C27.5831 18.9982 27.901 18.8665 28.1355 18.6321C28.3699 18.3977 28.5016 18.0798 28.5016 17.7482C28.5016 17.4167 28.3699 17.0988 28.1355 16.8643C27.901 16.6299 27.5831 16.4982 27.2516 16.4982"
                        fill="#1EB48F"
                      />
                    </svg>
                  </a>
                )}
              </div>
              <div className="license-text">
                <p>This work is licensed under a Creative Commons</p>
                <p>Attribution-NonCommercial-ShareAlike 3.0</p>
                <p>Unported License.</p>
              </div>
            </div>
          </div>

          <div className="legal">
            <div className="pages">
              {extraPages.map((page) => (
                <Link to={page} key={page.id} className="footer-extra-links">
                  {page.title}
                </Link>
              ))}
            </div>

            <div className="text">
              <a href="https://www.tectonica.co/" target="_blank">
                Built by Tectonica
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Footer;
